import { CaretRight as CaretRightIcon } from '@creditornot/cb-icons';
import { MenuActionItemVariant, MenuActionItemProps } from '@creditornot/cb-menu';
import { rootCss } from '@creditornot/cb-menu/lib/esm/MenuActionItem';
import React, { ReactNode } from 'react';
import styled from 'styled-components';

export type CaretPosition = 'left' | 'right';

export type Props = {
  active?: boolean;
  accordionContent?: React.ReactNode;
  caretPosition?: CaretPosition;
  children?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  onBlur?: React.FocusEventHandler<HTMLButtonElement>;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  onFocus?: React.FocusEventHandler<HTMLButtonElement>;
  open?: boolean;
  style?: React.CSSProperties;
  tabIndex?: number;
  variant?: MenuActionItemVariant;
  menuItemRef?: React.RefObject<HTMLButtonElement>;
  icon?: ReactNode;
};

const Root = styled.div`
  .ReactCollapse--collapse {
    transition: height 500ms cubic-bezier(0.16, 1, 0.3, 1);
  }
`;

const Item = styled.button<MenuActionItemProps>`
  ${rootCss};
`;

const LeftIconContainer = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding-inline-end: 8px;

  svg {
    width: 12px;
  }
`;

const RightIconContainer = styled(LeftIconContainer)`
  padding-inline-end: 0;
  padding-inline-start: 8px;
`;

const StyledCaretIcon = styled(CaretRightIcon)<{ direction: 'down' | 'right' }>`
  transition: transform 500ms cubic-bezier(0.16, 1, 0.3, 1);
  transform: ${({ direction, theme }) => {
    if (direction === 'down') {
      return 'rotate(90deg)';
    }
    return theme?.cbDirection === 'rtl' ? 'rotate(180deg)' : 'none';
  }};
`;

const ItemContent = styled.div`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CollapsedContent = React.memo(styled.div`
  padding-block-start: 8px;
  padding-inline-start: 28px;

  [data-cb-menu-item] {
    margin-inline-start: -8px;
    margin-inline-end: -8px;
  }

  > * + * {
    margin-block-start: 8px;
  }
`);

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-inline-end: 8px;

  & svg {
    width: 12px;
  }
`;

// Copied cb-MenuAccordionItem implemetation without react-collapse due performance issue
const MenuAccordionItem: React.FC<Props> = ({
  active = false,
  accordionContent,
  caretPosition = 'left',
  children,
  disabled = false,
  open = false,
  tabIndex = 0,
  variant = 'black',
  menuItemRef,
  icon,
  ...rest
}) => (
  <Root data-cb-menu-item>
    <Item
      selected={active}
      variant={variant}
      disabled={disabled}
      tabIndex={tabIndex}
      {...rest}
      ref={menuItemRef}
      type="button"
    >
      {caretPosition === 'left' && (
        <LeftIconContainer>
          <StyledCaretIcon direction={open ? 'down' : 'right'} />
        </LeftIconContainer>
      )}
      {icon && <IconContainer>{icon}</IconContainer>}
      <ItemContent>{children}</ItemContent>
      {caretPosition === 'right' && (
        <RightIconContainer>
          <StyledCaretIcon direction={open ? 'down' : 'right'} />
        </RightIconContainer>
      )}
    </Item>

    {open && <CollapsedContent>{accordionContent}</CollapsedContent>}
  </Root>
);

export default React.memo(MenuAccordionItem);
